import { useState, useEffect, useRef } from 'react';
import { PencilSimple } from '@phosphor-icons/react/dist/ssr';
import { Plus, DotsThreeVertical, Trash, Funnel } from 'phosphor-react';
import { CustomerCollectionView } from '@dill/dill-shared';
import { DillButton, DillModal, DillCheckbox, DillInput } from '../../../general/Dill';

// ── Top-level "quick filters" (above the column filters, always visible) ─────────────────────

export type AgingBucketKey = 'pastDue90' | 'pastDue61' | 'pastDue31' | 'pastDue1';

// Each toggled bucket OR's together (any selected bucket with a positive balance matches) - AND's
// against the other filter groups below (branch, credit manager, promise to pay).
export const BUCKET_QUICK_FILTERS: { key: AgingBucketKey; label: string }[] = [
  { key: 'pastDue90', label: '90+ Past Due' },
  { key: 'pastDue61', label: '61-90 Past Due' },
  { key: 'pastDue31', label: '31-60 Past Due' },
  { key: 'pastDue1', label: '1-30 Past Due' },
];

// Same OR-within-group behavior as the other filter groups: selecting only one reads as "must
// match this", selecting both is a no-op (matches everyone) rather than a contradiction.
export type PromiseToPayFilterValue = 'HAS_ACTIVE' | 'NO_ACTIVE';
export const PROMISE_TO_PAY_QUICK_FILTERS: { value: PromiseToPayFilterValue; label: string }[] = [
  { value: 'HAS_ACTIVE', label: 'Has an active Promise to Pay' },
  { value: 'NO_ACTIVE', label: 'Does not have an active Promise to Pay' },
];

// A single-value group so it reuses the exact same checkbox-group plumbing as the other filters
// (see quickFilterItems/handleQuickFilterChange/activeQuickFilterChips in Customers.tsx) rather
// than a one-off boolean. Whether a given customer IS flagged lives on the buyer doc itself
// (row.isFlagged, a shared team-wide marker - see buyerSchema.ts); this is just the filter toggle
// for "show only those". AUTOPAY sits in the same "Status" group and matches buyers with autopay
// turned on (row.isAutopayActive) - selections within the group OR together like the others.
export type FlagFilterValue = 'FLAGGED' | 'AUTOPAY';
export const FLAG_QUICK_FILTERS: { value: FlagFilterValue; label: string }[] = [
  { value: 'FLAGGED', label: 'Flagged' },
  { value: 'AUTOPAY', label: 'Autopay On' },
];

// Optional $ range narrowing a single bucket. min/max also accept `undefined` (not just `null`)
// so this stays assignable from the backend's CustomerCollectionViewFilters type, whose zod
// schema uses `.nullish()`.
export interface AmountRange {
  min?: number | null;
  max?: number | null;
}

export const EMPTY_AMOUNT_RANGE: AmountRange = { min: null, max: null };

export const isAmountRangeActive = (range: AmountRange | undefined) =>
  range != null && (range.min != null || range.max != null);

// Each selected bucket can carry its own independent $ range (only meaningful for buckets that
// are also in `buckets` - a range for a since-deselected bucket is pruned, never left dangling).
// A row matches if ANY selected bucket's own amount falls within ITS range (buckets with no
// range set just need a positive amount) - see the matching logic in quickFilteredRows in
// Customers.tsx.
export type BucketAmountRanges = Partial<Record<AgingBucketKey, AmountRange>>;

export interface QuickFilters {
  buckets: Set<AgingBucketKey>;
  bucketAmountRanges: BucketAmountRanges;
  branches: Set<string>;
  creditManagerIds: Set<string>;
  promiseToPay: Set<PromiseToPayFilterValue>;
  flagged: Set<FlagFilterValue>;
}

export const EMPTY_QUICK_FILTERS: QuickFilters = {
  buckets: new Set(),
  bucketAmountRanges: {},
  branches: new Set(),
  creditManagerIds: new Set(),
  promiseToPay: new Set(),
  flagged: new Set(),
};

// A plain-array mirror of QuickFilters (Sets aren't JSON-serializable) - this is what actually
// gets persisted as a Collection View's `filters` field on the backend.
export interface StoredQuickFilters {
  buckets: AgingBucketKey[];
  bucketAmountRanges: BucketAmountRanges;
  branches: string[];
  creditManagerIds: string[];
  promiseToPay: PromiseToPayFilterValue[];
  flagged: FlagFilterValue[];
}

export const quickFiltersToStored = (filters: QuickFilters): StoredQuickFilters => ({
  buckets: Array.from(filters.buckets),
  bucketAmountRanges: filters.bucketAmountRanges,
  branches: Array.from(filters.branches),
  creditManagerIds: Array.from(filters.creditManagerIds),
  promiseToPay: Array.from(filters.promiseToPay),
  flagged: Array.from(filters.flagged),
});

export const storedToQuickFilters = (stored: StoredQuickFilters): QuickFilters => ({
  buckets: new Set(stored.buckets ?? []),
  bucketAmountRanges: stored.bucketAmountRanges ?? {},
  branches: new Set(stored.branches ?? []),
  creditManagerIds: new Set(stored.creditManagerIds ?? []),
  promiseToPay: new Set(stored.promiseToPay ?? []),
  flagged: new Set(stored.flagged ?? []),
});

export const areStoredFiltersEqual = (a: StoredQuickFilters, b: StoredQuickFilters) => {
  const norm = (values: string[]) => [...values].sort().join('\u0001');
  const rangesEqual = (x?: AmountRange, y?: AmountRange) =>
    (x?.min ?? null) === (y?.min ?? null) && (x?.max ?? null) === (y?.max ?? null);
  const allBucketKeys = BUCKET_QUICK_FILTERS.map((bucket) => bucket.key);
  return (
    norm(a.buckets) === norm(b.buckets) &&
    allBucketKeys.every((key) =>
      rangesEqual(a.bucketAmountRanges?.[key], b.bucketAmountRanges?.[key])
    ) &&
    norm(a.branches) === norm(b.branches) &&
    norm(a.creditManagerIds) === norm(b.creditManagerIds) &&
    norm(a.promiseToPay) === norm(b.promiseToPay) &&
    norm(a.flagged) === norm(b.flagged)
  );
};

// ── "Collection Views" - named, switchable presets of the quick filters above ────────────────
// Unlike the single sticky selection above (which is just "whatever you left it on"), a view is
// an explicitly named, saved filter combination a collections user can jump back to - e.g.
// "Branch X Past Due 91+". Views only ever store filter *criteria*, never a snapshot of matching
// buyers, so switching to one re-evaluates it against the current buyer data every time (a buyer
// that moves from Branch X to Branch Y falls off a Branch X view on its own). Persisted in the
// backend (private per user, not shared across the supplier's team - see
// customerCollectionViewModel.ts), so views follow a user across browsers/devices.

// A view plus a human-readable one-liner of what it filters by (e.g. "91+ Past Due · Branch:
// Dallas"), shown on hover of its tab so you can tell what it does without switching to it.
export interface CustomerCollectionViewWithSummary extends CustomerCollectionView {
  summary: string;
}

export const formatAmountRangeLabel = (range: AmountRange): string => {
  const fmt = (n: number) => `$${n.toLocaleString('en-US')}`;
  if (range.min != null && range.max != null) return `${fmt(range.min)}–${fmt(range.max)}`;
  if (range.min != null) return `≥ ${fmt(range.min)}`;
  if (range.max != null) return `≤ ${fmt(range.max)}`;
  return '';
};

export const describeStoredFilters = (
  filters: StoredQuickFilters,
  creditManagerOptions: { label: string; value: string }[]
): string => {
  const parts: string[] = [];
  if (filters.buckets.length > 0) {
    parts.push(
      filters.buckets
        .map((key) => {
          const label = BUCKET_QUICK_FILTERS.find((b) => b.key === key)?.label ?? key;
          const range = filters.bucketAmountRanges?.[key];
          const rangeLabel = range ? formatAmountRangeLabel(range) : '';
          return rangeLabel ? `${label} (${rangeLabel})` : label;
        })
        .join(', ')
    );
  }
  if (filters.branches.length > 0) {
    parts.push(`Branch: ${filters.branches.join(', ')}`);
  }
  if (filters.creditManagerIds.length > 0) {
    const names = filters.creditManagerIds.map(
      (id) => creditManagerOptions.find((o) => o.value === id)?.label ?? id
    );
    parts.push(`Credit Manager: ${names.join(', ')}`);
  }
  if (filters.promiseToPay.length > 0) {
    parts.push(
      filters.promiseToPay
        .map((value) => PROMISE_TO_PAY_QUICK_FILTERS.find((p) => p.value === value)?.label ?? value)
        .join(', ')
    );
  }
  if (filters.flagged.length > 0) {
    parts.push(
      filters.flagged
        .map((value) => FLAG_QUICK_FILTERS.find((f) => f.value === value)?.label ?? value)
        .join(', ')
    );
  }
  return parts.length > 0 ? parts.join(' · ') : 'No filters';
};

// ─── Components ─────────────────────────────────────────────────────────────────

// Lets a user pick a name and the filters for a new Collection View in one step, instead of
// creating a blank tab and separately configuring it via the top Filters dropdown. Mirrors the
// same filter groups/values as that dropdown (see QuickFilters), just laid out as plain checkbox
// groups since they all need to be visible at once here rather than behind a single trigger. Also
// used for editing an existing view (mode="edit", pre-filled via initialName/initialFilters).
export function CollectionViewFormModal({
  mode,
  initialName,
  initialFilters,
  onClose,
  onSave,
  isSaving,
  branchOptions,
  creditManagerOptions,
}: {
  mode: 'create' | 'edit';
  initialName?: string;
  initialFilters?: StoredQuickFilters;
  onClose: () => void;
  onSave: (name: string, filters: StoredQuickFilters) => void;
  isSaving?: boolean;
  branchOptions: { label: string; value: string }[];
  creditManagerOptions: { label: string; value: string }[];
}) {
  const [name, setName] = useState(initialName ?? '');
  const [selected, setSelected] = useState<QuickFilters>(
    initialFilters ? storedToQuickFilters(initialFilters) : EMPTY_QUICK_FILTERS
  );

  const handleSave = () => {
    if (!name.trim()) return;
    onSave(name, quickFiltersToStored(selected));
  };

  return (
    <DillModal
      open
      onClose={onClose}
      title={mode === 'edit' ? 'Edit Collection View' : 'New Collection View'}
      subtitle="Save a named, reusable filter combination you can switch back to anytime."
      width="480px"
      footer={
        <>
          <DillButton variant="secondary" label="Cancel" onClick={onClose} isDisabled={isSaving} />
          <DillButton
            label={mode === 'edit' ? 'Save Changes' : 'Save View'}
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={!name.trim() || isSaving}
          />
        </>
      }>
      <div className="flex flex-col gap-4">
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
            View Name
          </label>
          <DillInput value={name} onChange={setName} placeholder="e.g. Branch X Past Due 91+" />
        </div>

        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
            Aging Bucket
          </label>
          <div className="flex flex-col gap-2.5">
            {BUCKET_QUICK_FILTERS.map((bucket) => {
              const isChecked = selected.buckets.has(bucket.key);
              const range = selected.bucketAmountRanges[bucket.key] ?? EMPTY_AMOUNT_RANGE;
              return (
                <div key={bucket.key} className="flex flex-col gap-1.5">
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer">
                    <DillCheckbox
                      isChecked={isChecked}
                      onChange={() =>
                        setSelected((prev) => {
                          const buckets = new Set(prev.buckets);
                          const bucketAmountRanges = { ...prev.bucketAmountRanges };
                          if (buckets.has(bucket.key)) {
                            buckets.delete(bucket.key);
                            delete bucketAmountRanges[bucket.key];
                          } else {
                            buckets.add(bucket.key);
                          }
                          return { ...prev, buckets, bucketAmountRanges };
                        })
                      }
                    />
                    {bucket.label}
                  </label>
                  {isChecked && (
                    <div className="flex items-center gap-2 pl-6">
                      <input
                        type="number"
                        value={range.min ?? ''}
                        onChange={(e) =>
                          setSelected((prev) => ({
                            ...prev,
                            bucketAmountRanges: {
                              ...prev.bucketAmountRanges,
                              [bucket.key]: {
                                ...range,
                                min: e.target.value === '' ? null : Number(e.target.value),
                              },
                            },
                          }))
                        }
                        placeholder="Min $"
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md outline-none"
                      />
                      <span className="text-gray-400 text-xs">–</span>
                      <input
                        type="number"
                        value={range.max ?? ''}
                        onChange={(e) =>
                          setSelected((prev) => ({
                            ...prev,
                            bucketAmountRanges: {
                              ...prev.bucketAmountRanges,
                              [bucket.key]: {
                                ...range,
                                max: e.target.value === '' ? null : Number(e.target.value),
                              },
                            },
                          }))
                        }
                        placeholder="Max $"
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md outline-none"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {branchOptions.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
              Branch
            </label>
            <DillCheckbox
              direction="vertical"
              options={branchOptions}
              value={Array.from(selected.branches)}
              onChange={(value: string[]) =>
                setSelected((prev) => ({ ...prev, branches: new Set(value) }))
              }
            />
          </div>
        )}

        {creditManagerOptions.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
              Credit Manager
            </label>
            <DillCheckbox
              direction="vertical"
              options={creditManagerOptions}
              value={Array.from(selected.creditManagerIds)}
              onChange={(value: string[]) =>
                setSelected((prev) => ({ ...prev, creditManagerIds: new Set(value) }))
              }
            />
          </div>
        )}

        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
            Promise to Pay
          </label>
          <DillCheckbox
            direction="vertical"
            options={PROMISE_TO_PAY_QUICK_FILTERS.map((p) => ({ label: p.label, value: p.value }))}
            value={Array.from(selected.promiseToPay)}
            onChange={(value: string[]) =>
              setSelected((prev) => ({
                ...prev,
                promiseToPay: new Set(value as PromiseToPayFilterValue[]),
              }))
            }
          />
        </div>

        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
            Status
          </label>
          <DillCheckbox
            direction="vertical"
            options={FLAG_QUICK_FILTERS.map((f) => ({ label: f.label, value: f.value }))}
            value={Array.from(selected.flagged)}
            onChange={(value: string[]) =>
              setSelected((prev) => ({ ...prev, flagged: new Set(value as FlagFilterValue[]) }))
            }
          />
        </div>
      </div>
    </DillModal>
  );
}

// A row of switchable, named filter presets above the table (mirrors the "Company Views" tab
// strip pattern) - "All Customers" is always first and isn't itself a CustomerCollectionView
// (it's the plain sticky quickFilters selection), followed by the user's saved views, then a
// "+" to save the current filter selection as a new one.
export function CollectionViewTabs({
  views,
  activeViewId,
  onSelectView,
  onRequestCreateView,
  onRequestEditView,
  onRenameView,
  onDeleteView,
}: {
  views: CustomerCollectionViewWithSummary[];
  activeViewId: string | null;
  onSelectView: (id: string | null) => void;
  onRequestCreateView: () => void;
  onRequestEditView: (id: string) => void;
  onRenameView: (id: string, name: string) => void;
  onDeleteView: (id: string) => void;
}) {
  const [editingViewId, setEditingViewId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const confirmRename = (id: string) => {
    if (editingName.trim()) onRenameView(id, editingName);
    setEditingViewId(null);
    setEditingName('');
  };

  const tabClass = (isActive: boolean) =>
    `px-3 py-1.5 text-xs font-semibold rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
      isActive
        ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700'
    }`;

  return (
    <div className="flex flex-col gap-1.5 mb-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={() => onSelectView(null)}
          className={tabClass(!activeViewId)}>
          All Customers
        </button>

        {views.map((view) => {
          const isActive = view.id === activeViewId;
          if (editingViewId === view.id) {
            return (
              <input
                key={view.id}
                autoFocus
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmRename(view.id);
                  if (e.key === 'Escape') setEditingViewId(null);
                }}
                onBlur={() => confirmRename(view.id)}
                className="px-2.5 py-1 text-xs font-medium rounded-full border border-blue-300 outline-none w-32"
              />
            );
          }
          return (
            <div key={view.id} className="relative flex items-center group">
              <button
                type="button"
                onClick={() => onSelectView(view.id)}
                title={view.summary}
                className={tabClass(isActive)}>
                {view.name}
              </button>
              <div
                className={`relative transition-opacity ${
                  openMenuId === view.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
                ref={openMenuId === view.id ? menuRef : undefined}>
                <button
                  type="button"
                  onClick={() => setOpenMenuId(openMenuId === view.id ? null : view.id)}
                  title="View options"
                  className="flex items-center justify-center bg-transparent border-none cursor-pointer p-0.5 text-gray-400 hover:text-gray-600">
                  <DotsThreeVertical size={14} weight="bold" />
                </button>
                {openMenuId === view.id && (
                  <div className="absolute left-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-36">
                    <button
                      type="button"
                      onClick={() => {
                        onRequestEditView(view.id);
                        setOpenMenuId(null);
                      }}
                      className="w-full flex items-center gap-2 text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                      <Funnel size={12} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingViewId(view.id);
                        setEditingName(view.name);
                        setOpenMenuId(null);
                      }}
                      className="w-full flex items-center gap-2 text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                      <PencilSimple size={12} /> Rename
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onDeleteView(view.id);
                        setOpenMenuId(null);
                      }}
                      className="w-full flex items-center gap-2 text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">
                      <Trash size={12} /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={onRequestCreateView}
          title="Create a new Collection View"
          className="flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-gray-300 text-gray-400 bg-transparent cursor-pointer hover:border-gray-400 hover:text-gray-600">
          <Plus size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}
